import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Loader2, CheckCircle2, CreditCard, Receipt as ReceiptIcon } from "lucide-react";
import { Button } from "../ui/button";
import { useUser } from "../../context/UserContext";
import { fetchInvoices, money, formatDate } from "../../lib/api";
import Receipt from "../common/Receipt";

const statusColor = (s) =>
  ({
    draft: "bg-gray-100 text-gray-600",
    sent: "bg-blue-100 text-blue-700",
    paid: "bg-green-100 text-green-700",
    void: "bg-red-50 text-red-500",
  }[s] || "bg-gray-100 text-gray-600");

export default function PortalInvoices() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    if (!user) return;
    fetchInvoices(user.email)
      .then(setInvoices)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-bold text-navy">My Invoices</h1>
        <p className="text-gray-500 text-sm mt-0.5">View and securely pay your invoices.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
      ) : invoices.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-200 p-12 text-center text-gray-400">
          <FileText className="h-10 w-10 mx-auto mb-3" />
          You have no invoices yet.
        </div>
      ) : (
        <div className="grid gap-4">
          {invoices.map((inv) => (
            <div key={inv.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-cream flex items-center justify-center">
                  <FileText className="h-6 w-6 text-gold-dark" />
                </div>
                <div>
                  <div className="font-semibold text-navy">{inv.number}</div>
                  <div className="text-xs text-gray-400">
                    {inv.items?.length || 0} item(s) · {formatDate(inv.created_at)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="font-serif text-xl font-bold text-navy">{money(inv.total)}</div>
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${statusColor(inv.status)}`}>{inv.status}</span>
                </div>
                {inv.status === "paid" ? (
                  <Button onClick={() => setReceipt(inv)} variant="outline" className="border-green-200 text-green-700 hover:bg-green-50" data-testid={`view-receipt-${inv.id}`}>
                    <ReceiptIcon className="h-4 w-4 mr-1.5" /> View Receipt
                  </Button>
                ) : (
                  <Button onClick={() => navigate(`/portal/pay/${inv.id}`)} className="bg-gold hover:bg-gold-dark text-navy font-semibold">
                    <CreditCard className="h-4 w-4 mr-1.5" /> Pay Now
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {receipt && <Receipt invoice={receipt} onClose={() => setReceipt(null)} />}
    </div>
  );
}
